import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useEmpresaContext } from '../context/EmpresaContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, Building2, Mail, Lock, AlertCircle, ArrowLeft, MessageCircle, Phone, Store, Globe, ShieldAlert, BookOpen } from 'lucide-react';
import axios from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';
import useDynamicFavicon from '../hooks/useDynamicFavicon';
import { primeraRutaPermitida } from '../utils/navPermisos';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const API = process.env.REACT_APP_BACKEND_URL;

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Colores del tema por defecto
const DEFAULT_COLORS = {
  primario: '#4A6B6B',
  secundario: '#3D5A5A',
  terciario: '#5A7A7A'
};

// Un fallo es "transitorio" (reintentable) si no hubo respuesta (timeout/reset/CORS)
// o si el origin/proxy devolvió 5xx — incluye los 520-524 de Cloudflare cuando el
// worker está momentáneamente saturado o reiniciándose.
const esErrorTransitorio = (err) => {
  if (!err?.response) return true;
  return err.response.status >= 500;
};

// GET con reintentos y backoff exponencial ante fallos transitorios del origin.
// No reintenta ante 4xx (p.ej. 404 empresa no encontrada).
const getConReintentos = async (url, { intentos = 3, baseDelay = 700 } = {}) => {
  let ultimoError;
  for (let i = 0; i < intentos; i++) {
    try {
      return await axios.get(url);
    } catch (err) {
      ultimoError = err;
      if (!esErrorTransitorio(err) || i === intentos - 1) throw err;
      await new Promise((r) => setTimeout(r, baseDelay * Math.pow(2, i)));
    }
  }
  throw ultimoError;
};

// Convierte cualquier error de red/servidor en un mensaje AMIGABLE, evitando pintar
// cuerpos crudos del proxy (p.ej. el aviso HTML/plano de un Cloudflare 520).
const mensajeAmigableLogin = (err) => {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === 'object' && detail.message) return detail.message;
  // Solo aceptamos un detail string si parece un mensaje limpio de la app.
  if (typeof detail === 'string' && detail.length < 200 && !/[<>]/.test(detail)) return detail;
  if (!err?.response) return 'No pudimos conectar con el servidor. Intenta nuevamente en unos segundos.';
  if (status >= 500) return 'El servicio no está disponible en este momento. Intenta nuevamente en unos segundos.';
  return 'Error al iniciar sesión';
};

const LoginPage = () => {
  const navigate = useNavigate();
  const { empresaCodigo } = useParams(); // Obtener código de empresa de la URL
  const { login, user } = useAuth();
  const { esDominioEmpresa, empresaDominio } = useEmpresaContext();
  
  const [empresas, setEmpresas] = useState([]);
  const [empresaDirecta, setEmpresaDirecta] = useState(null); // Empresa cargada por URL
  const [loading, setLoading] = useState(false);
  const [loadingEmpresas, setLoadingEmpresas] = useState(true);
  const [loadingEmpresaDirecta, setLoadingEmpresaDirecta] = useState(!!empresaCodigo);
  const [empresasError, setEmpresasError] = useState(false);
  const [empresaDirectaError, setEmpresaDirectaError] = useState(false);
  const [error, setError] = useState('');
  const [themeColors, setThemeColors] = useState(DEFAULT_COLORS);

  const [formData, setFormData] = useState({
    empresa: '',
    correo: '',
    password: '',
  });

  // Seguridad de login: captcha tras N fallos + estado de bloqueo de cuenta.
  const [requiereCaptcha, setRequiereCaptcha] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [mathCaptcha, setMathCaptcha] = useState(null);   // { token, pregunta }
  const [mathRespuesta, setMathRespuesta] = useState('');
  const recaptchaRef = React.useRef(null);
  const [bloqueo, setBloqueo] = useState(null);           // { permanente, segundos_restantes }
  const [desbloqueoMsg, setDesbloqueoMsg] = useState('');
  const [enviandoDesbloqueo, setEnviandoDesbloqueo] = useState(false);

  const cargarMathCaptcha = React.useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/auth/captcha`);
      setMathCaptcha(r.data);
      setMathRespuesta('');
    } catch (_) { /* noop */ }
  }, []);

  const activarCaptcha = React.useCallback(() => {
    setRequiereCaptcha(true);
    if (!RECAPTCHA_SITE_KEY) cargarMathCaptcha();
  }, [cargarMathCaptcha]);

  const solicitarDesbloqueo = async () => {
    setEnviandoDesbloqueo(true);
    setDesbloqueoMsg('');
    try {
      await axios.post(`${API}/api/auth/desbloqueo/solicitar`, {
        correo: formData.correo,
        empresa_codigo: empresaCodigoEfectivo || (formData.empresa !== '__superadmin__' ? formData.empresa : null),
      });
      setDesbloqueoMsg('Si el correo corresponde a una cuenta, te enviamos un enlace para desbloquearla.');
    } catch (_) {
      setDesbloqueoMsg('Si el correo corresponde a una cuenta, te enviamos un enlace para desbloquearla.');
    } finally {
      setEnviandoDesbloqueo(false);
    }
  };

  // Código de empresa efectivo: dominio > URL param
  const empresaCodigoEfectivo = esDominioEmpresa ? empresaDominio?.codigo : empresaCodigo;

  // Favicon dinámico: dominio > URL param > selección dropdown
  const faviconCodigo = empresaCodigoEfectivo || (formData.empresa !== '__superadmin__' ? formData.empresa : null);
  useDynamicFavicon(faviconCodigo, empresaDirecta?.logo_url || empresaDominio?.logo_url);

  // Redireccionar si ya está logueado (respeta destino guardado por deep-link)
  useEffect(() => {
    if (user) {
      let dest = null;
      try {
        dest = sessionStorage.getItem('ldrv_redirect_after_login');
        sessionStorage.removeItem('ldrv_redirect_after_login');
      } catch (_) {}
      navigate(dest || primeraRutaPermitida(user));
    }
  }, [user, navigate]);

  // Si hay empresa por dominio, usarla como empresa directa
  useEffect(() => {
    if (esDominioEmpresa && empresaDominio) {
      setEmpresaDirecta(empresaDominio);
      setFormData(prev => ({ ...prev, empresa: empresaDominio.codigo }));
      setLoadingEmpresaDirecta(false);
      if (empresaDominio.colores) {
        setThemeColors({
          primario: empresaDominio.colores.primario || DEFAULT_COLORS.primario,
          secundario: empresaDominio.colores.secundario || DEFAULT_COLORS.secundario,
          terciario: empresaDominio.colores.terciario || DEFAULT_COLORS.terciario
        });
      }
    }
  }, [esDominioEmpresa, empresaDominio]);

  // Cargar empresa directa por URL si hay código (solo si no hay dominio)
  const cargarEmpresaDirecta = React.useCallback(async () => {
    setLoadingEmpresaDirecta(true);
    setEmpresaDirectaError(false);
    setError('');
    try {
      const response = await getConReintentos(`${API_URL}/api/auth/empresa/${empresaCodigo}`);
      setEmpresaDirecta(response.data);
      setFormData(prev => ({ ...prev, empresa: response.data.codigo }));
      if (response.data.colores) {
        setThemeColors({
          primario: response.data.colores.primario || DEFAULT_COLORS.primario,
          secundario: response.data.colores.secundario || DEFAULT_COLORS.secundario,
          terciario: response.data.colores.terciario || DEFAULT_COLORS.terciario
        });
      }
    } catch (err) {
      console.error('Error fetching empresa:', err);
      if (err?.response?.status === 404) {
        // Empresa realmente inexistente: informar y volver al login general.
        setError('Empresa no encontrada. Seleccione una empresa de la lista.');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        // Fallo transitorio del servidor (520/5xx/timeout): NO redirigir (perdería el
        // contexto de la empresa). Mostrar error reintentable.
        setEmpresaDirectaError(true);
      }
    } finally {
      setLoadingEmpresaDirecta(false);
    }
  }, [empresaCodigo, navigate]);

  useEffect(() => {
    if (empresaCodigo && !esDominioEmpresa) {
      cargarEmpresaDirecta();
    }
  }, [empresaCodigo, esDominioEmpresa, cargarEmpresaDirecta]);

  // Cargar lista de empresas (solo si no hay empresa directa)
  const cargarEmpresas = React.useCallback(async () => {
    setLoadingEmpresas(true);
    setEmpresasError(false);
    try {
      const response = await getConReintentos(`${API_URL}/api/auth/empresas`);
      setEmpresas(response.data);
    } catch (err) {
      console.error('Error fetching empresas:', err);
      setEmpresasError(true);
    } finally {
      setLoadingEmpresas(false);
    }
  }, []);

  useEffect(() => {
    if (!empresaCodigoEfectivo) {
      cargarEmpresas();
    } else {
      setLoadingEmpresas(false);
    }
  }, [empresaCodigoEfectivo, cargarEmpresas]);

  // Actualizar colores cuando se selecciona una empresa del dropdown
  const handleEmpresaChange = (empresaCodigoSelected) => {
    setFormData({ ...formData, empresa: empresaCodigoSelected });
    
    const selectedEmpresa = empresas.find(e => e.codigo === empresaCodigoSelected);
    if (selectedEmpresa?.colores) {
      setThemeColors({
        primario: selectedEmpresa.colores.primario || DEFAULT_COLORS.primario,
        secundario: selectedEmpresa.colores.secundario || DEFAULT_COLORS.secundario,
        terciario: selectedEmpresa.colores.terciario || DEFAULT_COLORS.terciario
      });
    } else {
      setThemeColors(DEFAULT_COLORS);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setDesbloqueoMsg('');
    setLoading(true);

    // Adjuntar captcha si es requerido
    const extra = {};
    if (requiereCaptcha) {
      if (RECAPTCHA_SITE_KEY) {
        if (!recaptchaToken) {
          setError('Completa la verificación de seguridad (captcha).');
          setLoading(false);
          return;
        }
        extra.recaptcha_token = recaptchaToken;
      } else {
        if (!mathCaptcha?.token || !mathRespuesta) {
          setError('Responde la verificación de seguridad.');
          setLoading(false);
          return;
        }
        extra.captcha_token = mathCaptcha.token;
        extra.captcha_respuesta = mathRespuesta;
      }
    }

    try {
      const data = await login(formData.empresa, formData.correo, formData.password, extra);
      navigate(primeraRutaPermitida(data?.user));
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      const msg = (detail && typeof detail === 'object') ? detail.message : (typeof detail === 'string' ? detail : null);

      // Cuenta bloqueada (423)
      if (status === 423 || (detail && typeof detail === 'object' && detail.locked)) {
        setBloqueo({
          permanente: !!detail?.permanente,
          segundos_restantes: detail?.segundos_restantes || 0,
        });
        activarCaptcha();
        setError(msg || 'Cuenta bloqueada por seguridad.');
      } else {
        // Captcha requerido tras N fallos
        if (detail && typeof detail === 'object' && detail.requiere_captcha) {
          activarCaptcha();
        }
        setError(mensajeAmigableLogin(err));
      }
      // Reset del widget reCAPTCHA para permitir reintento
      setRecaptchaToken(null);
      try { recaptchaRef.current?.reset?.(); } catch (_) {}
      if (requiereCaptcha && !RECAPTCHA_SITE_KEY) cargarMathCaptcha();
    } finally {
      setLoading(false);
    }
  };

  // Determinar si mostrar el dropdown o no
  const showDropdown = !empresaCodigoEfectivo || !empresaDirecta;
  
  // Obtener datos de la empresa para mostrar (directa o seleccionada)
  const empresaActual = empresaDirecta || empresas.find(e => e.codigo === formData.empresa);

  // Loading mientras carga empresa directa
  if (loadingEmpresaDirecta && !esDominioEmpresa) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
          <p className="mt-2 text-slate-500">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Editorial (foto luminosa + wash de marca en degradado + glow) */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden">
        {/* Foto de fondo con brillo/saturación para darle vida */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/4222ef18-5620-4a3f-a717-274fdc803e45/images/bb8c75986004c424f191b43d37a4ba3e9beb3be78971e98e4c10170e153a33eb.jpeg)',
            filter: 'brightness(1.06) saturate(1.12)'
          }}
        />
        {/* Wash del color de marca (terciario, dinámico por empresa) en degradado */}
        <div
          className="absolute inset-0"
          style={{ background: `linear-gradient(135deg, ${themeColors.terciario}B3 0%, ${themeColors.terciario}DB 100%)` }}
        />
        {/* Glow cálido de acento (secundario de la empresa) */}
        <div
          className="absolute inset-0"
          style={{ background: `radial-gradient(60% 55% at 85% 12%, ${themeColors.secundario}59 0%, transparent 60%)` }}
        />
        <div className="relative z-10 flex flex-col justify-center w-full px-14 xl:px-20 text-white">
          <div className="max-w-xl">
            <h1 className="font-heading text-6xl md:text-7xl xl:text-8xl font-black tracking-tight mb-6 drop-shadow-sm">
              CXPLUS
            </h1>
            <p className="text-xl xl:text-2xl text-white/95 font-medium leading-relaxed mb-14 max-w-lg">
              Plataforma de Gestión de Experiencia de Clientes Omnicanal Avanzada
            </p>
            <div className="grid grid-cols-3 xl:grid-cols-6 gap-6 xl:gap-8 w-fit">
              {[
                { Icon: Mail, label: 'Correo' },
                { Icon: Store, label: 'Presencial' },
                { Icon: MessageCircle, label: 'Chat' },
                { Icon: Phone, label: 'Teléfono' },
                { Icon: Globe, label: 'Autoservicio' },
                { Icon: BookOpen, label: 'Reclamaciones' },
              ].map(({ Icon, label }) => (
                <div key={label} className="flex flex-col items-center gap-2.5">
                  <span className="w-14 h-14 rounded-full flex items-center justify-center border border-white/40 bg-white/15 backdrop-blur-sm shadow-sm">
                    <Icon className="w-6 h-6 text-white" />
                  </span>
                  <span className="text-sm text-white/90 tracking-wide">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Form (editorial, inline sobre blanco cálido) */}
      <div className="flex-1 flex flex-col bg-stone-50">
        <div className="flex-1 flex items-center justify-center px-8 py-10">
        <Card className="w-full max-w-md bg-white border border-slate-200 shadow-xl rounded-2xl">
          <CardHeader className="space-y-1 text-center">
            {/* Logo o Ícono */}
            <div className="flex justify-center mb-4">
              {empresaActual?.logo_url ? (
                <div className="w-20 h-20 rounded-xl flex items-center justify-center overflow-hidden bg-white border border-slate-200 p-2">
                  <img 
                    src={empresaActual.logo_url} 
                    alt={`Logo ${empresaActual.nombre}`}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              ) : (
                <div 
                  className="w-16 h-16 rounded-xl flex items-center justify-center transition-colors duration-200"
                  style={{ backgroundColor: themeColors.terciario }}
                >
                  <Building2 className="w-8 h-8 text-white" />
                </div>
              )}
            </div>
            
            <CardTitle className="font-heading text-3xl font-bold">
              Panel de Administración
            </CardTitle>
            
            {/* Nombre de la empresa si está seleccionada */}
            {empresaActual && empresaActual.codigo !== '__superadmin__' && (
              <p 
                className="text-sm font-medium"
                style={{ color: themeColors.terciario }}
              >
                {empresaActual.nombre}
              </p>
            )}
            
            <CardDescription>
              Ingrese sus credenciales para acceder
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive" className="animate-fadeIn">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Fallo transitorio al cargar catálogos (empresa/lista): reintentable */}
              {(empresasError || empresaDirectaError) && (
                <Alert variant="destructive" className="animate-fadeIn" data-testid="carga-empresas-error">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="flex items-center justify-between gap-3">
                    <span>
                      No pudimos cargar {empresaDirectaError ? 'los datos de la empresa' : 'la lista de empresas'}.
                      El servicio puede estar ocupado.
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      data-testid="reintentar-empresas-btn"
                      disabled={loadingEmpresas || loadingEmpresaDirecta}
                      onClick={() => (empresaDirectaError ? cargarEmpresaDirecta() : cargarEmpresas())}
                    >
                      {(loadingEmpresas || loadingEmpresaDirecta) ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : 'Reintentar'}
                    </Button>
                  </AlertDescription>
                </Alert>
              )}

              {/* Dropdown de empresa - solo si no viene por URL */}
              {showDropdown && (
                <div className="space-y-2">
                  <Label htmlFor="empresa" className="text-sm font-medium text-slate-700">
                    Empresa
                  </Label>
                  <Select
                    value={formData.empresa}
                    onValueChange={handleEmpresaChange}
                  >
                    <SelectTrigger 
                      id="empresa"
                      data-testid="empresa-select"
                      className="h-12 border-slate-200"
                      style={{ 
                        '--tw-ring-color': themeColors.primario 
                      }}
                    >
                      <SelectValue placeholder={loadingEmpresas ? "Cargando..." : (empresasError ? "No disponible — reintenta" : "Seleccione una empresa")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__superadmin__">SUPERADMIN (Sin empresa)</SelectItem>
                      {empresas.map((emp) => (
                        <SelectItem key={emp.id} value={emp.codigo}>
                          {emp.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="correo" className="text-sm font-medium text-slate-700">
                  Correo electrónico
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="correo"
                    type="email"
                    placeholder="usuario@empresa.com"
                    data-testid="correo-input"
                    className="pl-10 h-12 border-slate-200"
                    value={formData.correo}
                    onChange={(e) => setFormData({ ...formData, correo: e.target.value })}
                    required
                    style={{ 
                      '--tw-ring-color': themeColors.primario 
                    }}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                  Contraseña
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    data-testid="password-input"
                    className="pl-10 h-12 border-slate-200"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    style={{ 
                      '--tw-ring-color': themeColors.primario 
                    }}
                  />
                </div>
              </div>

              {/* Verificación de seguridad (captcha) tras varios intentos fallidos */}
              {requiereCaptcha && (
                <div className="space-y-2" data-testid="login-captcha">
                  {RECAPTCHA_SITE_KEY ? (
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={RECAPTCHA_SITE_KEY}
                      onChange={(t) => setRecaptchaToken(t)}
                    />
                  ) : (
                    <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
                      <Label className="text-sm font-medium text-slate-700">
                        Verificación: {mathCaptcha?.pregunta || 'cargando…'}
                      </Label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        placeholder="Tu respuesta"
                        data-testid="login-captcha-input"
                        className="h-11 mt-2 border-slate-200"
                        value={mathRespuesta}
                        onChange={(e) => setMathRespuesta(e.target.value)}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Cuenta bloqueada por seguridad */}
              {bloqueo && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="login-bloqueo">
                  <div className="flex items-start gap-2">
                    <ShieldAlert className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-amber-800">
                      <p className="font-semibold">Tu cuenta está bloqueada por seguridad.</p>
                      {bloqueo.permanente ? (
                        <p>Requiere desbloqueo por correo o por un administrador.</p>
                      ) : (
                        <p>Puedes esperar unos minutos o desbloquearla por correo ahora.</p>
                      )}
                      <button
                        type="button"
                        onClick={solicitarDesbloqueo}
                        disabled={enviandoDesbloqueo || !formData.correo}
                        data-testid="login-solicitar-desbloqueo-btn"
                        className="mt-2 underline font-medium disabled:opacity-50"
                      >
                        {enviandoDesbloqueo ? 'Enviando…' : 'Enviar enlace de desbloqueo a mi correo'}
                      </button>
                      {desbloqueoMsg && <p className="mt-2 text-emerald-700">{desbloqueoMsg}</p>}
                    </div>
                  </div>
                </div>
              )}

              <Button
                type="submit"
                className="w-full h-12 text-white font-semibold transition-all duration-200"
                style={{ 
                  backgroundColor: themeColors.primario,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = themeColors.secundario;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = themeColors.primario;
                }}
                disabled={loading || (!empresaCodigoEfectivo && !formData.empresa)}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Iniciando sesión...
                  </>
                ) : (
                  'Iniciar Sesión'
                )}
              </Button>

            </form>
          </CardContent>
        </Card>
        </div>
        <footer className="pb-6 px-8 text-center text-xs text-slate-400">
          CXPLUS © {new Date().getFullYear()} - {(empresaActual && empresaActual.codigo !== '__superadmin__') ? empresaActual.nombre : 'Experiencia de Clientes Omnicanal Avanzada'}
        </footer>
      </div>
    </div>
  );
};

export default LoginPage;
